
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, registerSchema, Login, Register, UserRole } from "@webready/shared/schema";
import { useAuth } from "@/hooks/useAuth";
import { Facebook, Loader2, Info } from "lucide-react";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'login' | 'register';
  onSwitchType: () => void;
}

export default function AuthModal({ isOpen, onClose, type, onSwitchType }: AuthModalProps) {
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>(UserRole.PATIENT);

  // Login form
  const loginForm = useForm<Login>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Register form
  const registerForm = useForm<Register>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: UserRole.PATIENT,
    },
  });
  
  // Update form when role changes
  useEffect(() => {
    registerForm.setValue("role", selectedRole);
  }, [selectedRole, registerForm]);

  const handleLogin = async (data: Login) => {
    try {
      setIsLoading(true);
      const userData = await login(data.email, data.password);
      
      // Check if login was rejected (user doesn't exist in client schema)
      if (!userData) {
        console.log('Login rejected - user does not exist in client schema');
        // Toast is already shown in AuthContext
        return; // Don't navigate or show success toast
      }
      
      // Immediately determine the target route
      let targetRoute = '/dashboard/patient'; // Default
      
      console.log('Login successful, user role:', userData.role);
      switch (userData.role) {
        case UserRole.ADMIN:
          targetRoute = '/dashboard/admin';
          break;
        case UserRole.EMPLOYEE:
          targetRoute = '/dashboard/employee';
          break;
        case UserRole.PATIENT:
        default:
          targetRoute = '/dashboard/patient';
          break;
      }
      
      console.log('Attempting navigation to:', targetRoute);
      
      // Close the modal AFTER determining the route
      onClose();
      
      // Check if we're in public entry point - if so, redirect to dashboard entry
      // This ensures proper separation between public and dashboard bundles
      if (typeof window !== 'undefined' && !window.location.pathname.includes('dashboard-entry.html')) {
        // We're in public entry, need to redirect to dashboard entry
        setTimeout(() => {
          window.location.href = `/dashboard-entry.html#${targetRoute}`;
        }, 50);
      } else {
        // We're already in dashboard entry, use normal navigation
        setTimeout(() => {
          navigate(targetRoute, { replace: true });
          console.log('Navigation executed to:', targetRoute);
        }, 50);
      }
      
      toast({
        title: "Success!",
        description: "You have been logged in successfully.",
      });
    } catch (error: any) {
      console.error('Login error:', error);
      toast({
        title: "Login failed",
        description: error.message || "An error occurred during login.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (data: Register) => {
    try {
      setIsLoading(true);
      
      // Validate secret code for employee and admin accounts
      if (data.role !== UserRole.PATIENT && !data.secretCode) {
        throw new Error('Authorization code is required for employee and administrator accounts');
      }
      
      await register(data);
      onClose();
      
      // Navigate to the appropriate dashboard based on role
      const dashboardPath = 
        data.role === UserRole.ADMIN 
          ? "/dashboard/admin" 
          : data.role === UserRole.EMPLOYEE 
            ? "/dashboard/employee" 
            : "/dashboard/patient";
            
      navigate(dashboardPath);
      
      toast({
        title: "Success!",
        description: `Your account has been created successfully as a ${data.role}.`,
      });
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: error.message || "An error occurred during registration.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Custom onClose handler: dacă suntem pe /login, redirect către /
  const handleClose = () => {
    onClose();
    if (location === "/login") {
      setTimeout(() => navigate("/", { replace: true }), 50);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {type === 'login' ? 'Log In' : 'Create Account'}
          </DialogTitle>
          <DialogDescription>
            {type === 'login' ? 'Enter your credentials to access your account' : 'Fill in your information to create a new account'}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue={type} className="w-full" onValueChange={(value) => {
          if (value === 'login' && type === 'register') onSwitchType();
          if (value === 'register' && type === 'login') onSwitchType();
        }}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>
          
          <TabsContent value="login">
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  {...loginForm.register("email")}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-sm text-red-500">{loginForm.formState.errors.email.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  {...loginForm.register("password")}
                />
                {loginForm.formState.errors.password && (
                  <p className="text-sm text-red-500">{loginForm.formState.errors.password.message}</p>
                )}
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="remember"
                    className="h-4 w-4 text-primary border-slate-300 rounded focus:ring-primary"
                  />
                  <Label htmlFor="remember" className="text-sm text-slate-600">Remember me</Label>
                </div>
                <a href="#" className="text-sm text-primary hover:text-primary/80">Forgot password?</a>
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
            
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 bg-white text-sm text-slate-500">Or continue with</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mt-6">
                <Button variant="outline" type="button" disabled={isLoading}>
                  <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
                    <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
                  </svg>
                  Google
                </Button>
                <Button variant="outline" type="button" disabled={isLoading}>
                  <Facebook className="mr-2 h-4 w-4" />
                  Facebook
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="register">
            <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    type="text"
                    placeholder="John"
                    {...registerForm.register("first_name")}
                  />
                  {registerForm.formState.errors.first_name && (
                    <p className="text-sm text-red-500">{registerForm.formState.errors.first_name.message}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    type="text"
                    placeholder="Doe"
                    {...registerForm.register("last_name")}
                  />
                  {registerForm.formState.errors.last_name && (
                    <p className="text-sm text-red-500">{registerForm.formState.errors.last_name.message}</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="registerEmail">Email</Label>
                <Input
                  id="registerEmail"
                  type="email"
                  placeholder="your@email.com"
                  {...registerForm.register("email")}
                />
                {registerForm.formState.errors.email && (
                  <p className="text-sm text-red-500">{registerForm.formState.errors.email.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  placeholder="(123) 456-7890"
                  {...registerForm.register("phone")}
                />
                {registerForm.formState.errors.phone && (
                  <p className="text-sm text-red-500">{registerForm.formState.errors.phone.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Account Type</Label>
                <Select 
                  defaultValue={UserRole.PATIENT}
                  onValueChange={(value) => setSelectedRole(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UserRole.PATIENT}>Patient</SelectItem>
                    <SelectItem value={UserRole.EMPLOYEE}>Employee</SelectItem>
                    <SelectItem value={UserRole.ADMIN}>Administrator</SelectItem>
                  </SelectContent>
                </Select>
                {selectedRole !== UserRole.PATIENT && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <Info className="h-3 w-3 inline-block mr-1" />
                    Employee and administrator accounts require authorization code
                  </p>
                )}
              </div>
              
              {selectedRole !== UserRole.PATIENT && (
                <div className="space-y-2">
                  <Label htmlFor="secretCode">Authorization Code</Label>
                  <Input
                    id="secretCode"
                    type="password"
                    placeholder="Enter authorization code"
                    {...registerForm.register("secretCode")}
                  />
                  {registerForm.formState.errors.secretCode && (
                    <p className="text-sm text-red-500">{registerForm.formState.errors.secretCode.message}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="registerPassword">Password</Label>
                <Input
                  id="registerPassword"
                  type="password"
                  placeholder="••••••••"
                  {...registerForm.register("password")}
                />
                {registerForm.formState.errors.password && (
                  <p className="text-sm text-red-500">{registerForm.formState.errors.password.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  {...registerForm.register("confirmPassword")}
                />
                {registerForm.formState.errors.confirmPassword && (
                  <p className="text-sm text-red-500">{registerForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>
            
            <p className="mt-4 text-center text-sm text-slate-600">
              By registering, you agree to our{" "}
              <a href="#" className="text-primary hover:text-primary/80 font-medium">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="#" className="text-primary hover:text-primary/80 font-medium">
                Privacy Policy
              </a>
              .
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
